import { Trans, useTranslation } from 'react-i18next';
import { usePrevisualitzaSyncUsuari, useSyncUsuari } from '@/api/ajustosPersones';
import { Icona } from '@/components/Icona';
import { Calaix } from '../marc/Calaix';
import { useHiHaCanvis } from '../marc/canvisContext';
import { ErrorDesa } from '../comu';

/**
 * Sincronització d'una persona amb Entra ID: abans d'aplicar-la mostra quines dades del CAI
 * se substituiran pels valors d'Entra i demana confirmació explícita.
 */
export function SyncEntra({
  usuariId,
  nom,
  onTanca,
  onFet,
}: {
  usuariId: number;
  nom: string;
  onTanca: () => void;
  onFet: () => void;
}) {
  const { t } = useTranslation('administracio');
  const previa = usePrevisualitzaSyncUsuari(usuariId, true);
  const sync = useSyncUsuari(usuariId);
  const pendents = useHiHaCanvis();
  const dif = previa.data?.diferencies ?? [];
  const nomCamp = (c: string) => t(`persones.perfil.camps.${c}`, { defaultValue: c });

  return (
    <Calaix
      titol={t('persones.sync.titol')}
      etiqueta={nom}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('marc.confirma.cancella')}
          </button>
          <button
            type="button"
            className={`btn btn--s ${dif.length ? 'btn--critic' : 'btn--dark'}`}
            disabled={!previa.isSuccess || sync.isPending}
            onClick={() => sync.mutate(undefined, { onSuccess: onFet })}
          >
            <Icona nom="arrow-clockwise" />
            {dif.length
              ? t('persones.sync.substitueix', { count: dif.length })
              : t('persones.sync.sincronitza')}
          </button>
        </>
      }
    >
      {previa.isLoading ? (
        <p className="dim">{t('persones.sync.carregant')}</p>
      ) : previa.isError ? (
        <ErrorDesa error={previa.error} />
      ) : dif.length === 0 ? (
        <div className="info-banda">
          <Icona nom="checkmark-circle" />
          {t('persones.sync.senseDiferencies')}
        </div>
      ) : (
        <>
          <div className="avis-banda" role="alert">
            <Icona nom="exclamationmark-triangle" />
            <span>
              <Trans
                t={t}
                i18nKey="persones.sync.avis"
                count={dif.length}
                components={{ b: <strong /> }}
              />
            </span>
          </div>
          <div className="panel">
            <table className="taula sync-diff">
              <thead>
                <tr>
                  <th>{t('persones.sync.dada')}</th>
                  <th>{t('persones.sync.ara')}</th>
                  <th>{t('persones.sync.quedara')}</th>
                </tr>
              </thead>
              <tbody>
                {dif.map((d) => (
                  <tr key={d.camp} data-camp={d.camp}>
                    <td>{nomCamp(d.camp)}</td>
                    <td className="sync-diff__abans">{d.valorCai || '—'}</td>
                    <td className="strong">{d.valorEntra || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {pendents ? <p className="t-footnote dim">{t('persones.sync.descartaPendents')}</p> : null}
      <p className="t-footnote dim">{t('persones.sync.noToca')}</p>
      <ErrorDesa error={sync.error} />
    </Calaix>
  );
}
